import React, { createContext, useContext, useState, useEffect } from 'react';
import HttpApis from './services/HttpApis';
import { useTenant } from './TenantContext';
import { applyTheme } from './theme/applyTheme';
import { applyDocumentMeta } from './theme/applyDocumentMeta';

const ConfigContext = createContext(null);

export const ConfigProvider = ({ children }) => {
  const [config, setConfig] = useState(null);

  // Apply env/default brand color immediately so the UI is themed
  // before the /v1/config network call resolves (avoids a color flash).
  useEffect(() => {
    applyTheme(null);
  }, []);

  // Wait for tenant-info: until it resolves, HttpApis has no tenant id or
  // backendUrl and would read the default backend's config instead (wrong
  // for tenants on their own backend, e.g. custom instances).
  const { loading: tenantLoading, logoUrl } = useTenant();

  // Title, description, theme-color and favicons from the tenant config
  // (env/defaults while loading or if /v1/config fails), so no literal
  // %REACT_APP_...% placeholder from public/index.html survives in <head>.
  useEffect(() => {
    applyDocumentMeta(config, logoUrl);
  }, [config, logoUrl]);

  useEffect(() => {
    if (tenantLoading) return;
    const fetchConfig = async () => {
      try {
        const response = await HttpApis.getConfig();
        if (response.data.status && response.data.data) {
          setConfig(response.data.data);
          // Re-theme from the per-tenant config returned by the API.
          applyTheme(response.data.data);
        } else {
          // Settle to defaults so consumers waiting on `config !== null`
          // (e.g. the player's fetch effects) don't wait forever.
          setConfig({});
        }
      } catch (err) {
        console.error(err);
        setConfig({});
      }
    };
    fetchConfig();
  }, [tenantLoading]);

  return (
    <ConfigContext.Provider value={config}>
      {children}
    </ConfigContext.Provider>
  );
};

export const useConfig = () => {
  return useContext(ConfigContext);
};